import { Directory, Encoding, Filesystem } from '@capacitor/filesystem'

export type QueuedPhotoState = 'queued' | 'uploading' | 'failed'

export type QueuedPhotoEntry = {
  clientId: string
  orderId: number
  contentType: string
  width: number
  height: number
  /** Real capture time — null for library picks (shot at an unknown time). */
  takenAtIso: string | null
  enqueuedAtIso: string
  /** Operator who took it — the drain only uploads under the same session. */
  userId: number | null
  /** Organization it was taken in — order ids are per tenant. */
  tid: number
  attempts: number
  nextAttemptAtIso: string | null
  lastError: string | null
  state: QueuedPhotoState
}

/**
 * Pending-photo store in Filesystem `Directory.Data`: survives Capgo web
 * bundle swaps and the `localStorage.clear()` wipe paths. Each entry is its
 * own `<clientId>.meta.json` sidecar (no central index) so a crash mid-write
 * can corrupt at most one entry, never the whole queue.
 */
const QUEUE_DIR = 'order-photos'

const metaPath = (clientId: string) => `${QUEUE_DIR}/${clientId}.meta.json`
const photoPath = (clientId: string) => `${QUEUE_DIR}/${clientId}.jpg`
const thumbPath = (clientId: string) => `${QUEUE_DIR}/${clientId}.thumb.jpg`

export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  const chunkSize = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return btoa(binary)
}

export function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }
  return new Blob([bytes], { type: contentType })
}

export async function writeQueuedPhotoMeta(
  entry: QueuedPhotoEntry,
): Promise<void> {
  await Filesystem.writeFile({
    path: metaPath(entry.clientId),
    data: JSON.stringify(entry),
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true,
  })
}

/** Binaries first: meta is written last so a crash never leaves a visible
 * entry whose files are missing (orphan binaries are invisible and cheap). */
export async function enqueuePhoto(
  entry: QueuedPhotoEntry,
  photoBlob: Blob,
  thumbBlob: Blob,
): Promise<void> {
  try {
    await Filesystem.writeFile({
      path: photoPath(entry.clientId),
      data: await blobToBase64(photoBlob),
      directory: Directory.Data,
      recursive: true,
    })
    await Filesystem.writeFile({
      path: thumbPath(entry.clientId),
      data: await blobToBase64(thumbBlob),
      directory: Directory.Data,
      recursive: true,
    })
    await writeQueuedPhotoMeta(entry)
  } catch (error) {
    // A half-written entry has no meta, so nothing could ever reclaim its
    // multi-MB binaries — clean up before surfacing (out-of-storage is the
    // realistic trigger, exactly when leaks compound).
    await removeQueuedPhoto(entry.clientId)
    throw error
  }
}

// Only a missing queue directory means "empty queue" — permissions or plugin
// failures must surface, or the drain silently lies that nothing is pending.
const isMissingDirectory = (error: unknown): boolean =>
  error instanceof Error && /exist|ENOENT/i.test(error.message)

/** FIFO by enqueue time. Unreadable metas are skipped, never deleted. */
export async function listQueuedPhotos(): Promise<QueuedPhotoEntry[]> {
  let names: string[]
  try {
    const { files } = await Filesystem.readdir({
      path: QUEUE_DIR,
      directory: Directory.Data,
    })
    names = files.map((file) => file.name)
  } catch (error) {
    if (isMissingDirectory(error)) return [] // not created yet
    throw error
  }

  const entries: QueuedPhotoEntry[] = []
  for (const name of names.filter((n) => n.endsWith('.meta.json'))) {
    try {
      const { data } = await Filesystem.readFile({
        path: `${QUEUE_DIR}/${name}`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      })
      const parsed = JSON.parse(String(data)) as QueuedPhotoEntry
      // Valid JSON with the wrong shape must not wedge the sort below —
      // one bad entry (e.g. written by an older schema) may cost itself,
      // never the whole queue.
      // Every field the drain reads or computes on (a NaN attempts count
      // would throw inside the drain's own catch and wedge the queue)
      const hasRequiredShape =
        typeof parsed?.clientId === 'string' &&
        typeof parsed?.enqueuedAtIso === 'string' &&
        typeof parsed?.orderId === 'number' &&
        typeof parsed?.tid === 'number' &&
        typeof parsed?.contentType === 'string' &&
        typeof parsed?.attempts === 'number' &&
        typeof parsed?.state === 'string'
      if (!hasRequiredShape) {
        console.error('[photoQueue] skipping malformed meta', name)
        continue
      }
      entries.push(parsed)
    } catch (error) {
      console.error('[photoQueue] skipping unreadable meta', name, error)
    }
  }
  return entries.sort((a, b) => a.enqueuedAtIso.localeCompare(b.enqueuedAtIso))
}

/** Meta first: once the meta is gone the entry is invisible, so leftover
 * binaries from a partial remove are harmless orphans. */
export async function removeQueuedPhoto(clientId: string): Promise<void> {
  for (const path of [
    metaPath(clientId),
    photoPath(clientId),
    thumbPath(clientId),
  ]) {
    try {
      await Filesystem.deleteFile({ path, directory: Directory.Data })
    } catch (error) {
      console.error('[photoQueue] could not delete', path, error)
    }
  }
}

export async function readQueuedPhotoBlobs(
  clientId: string,
  contentType: string,
): Promise<{ photo: Blob; thumb: Blob }> {
  const [photo, thumb] = await Promise.all([
    Filesystem.readFile({
      path: photoPath(clientId),
      directory: Directory.Data,
    }),
    Filesystem.readFile({
      path: thumbPath(clientId),
      directory: Directory.Data,
    }),
  ])
  return {
    photo: base64ToBlob(String(photo.data), contentType),
    thumb: base64ToBlob(String(thumb.data), 'image/jpeg'),
  }
}
